import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    AbilityInput,
    CharacterType, Item, Note,
    SetCharacterSheetValueInput,
    SkillInput,
    Spell
} from "./characterType";
import {CharacterSheet} from '../../../Components/Content/Kampagne/KampagneTabs/CharacterSheet';
import {emptyCharacter} from "../../../Helper/Constants";

export const characterSlice = createSlice({
    name: 'character',
    initialState: emptyCharacter,
    reducers: {
        setCharacter: (state, action: PayloadAction<CharacterType>) => {
            state.characterId = action.payload.characterId;
            state.characterName = action.payload.characterName;
            state.characterExp = action.payload.characterExp;
            state.characterLevel = action.payload.characterLevel;
            state.character_to_class = action.payload.character_to_class;
            state.Race = action.payload.Race;
            state.Subrace = action.payload.Subrace;
            state.CharacterSheet = action.payload.CharacterSheet
        },

        setCharacterValues: (state, action: PayloadAction<{ characterName: string, characterId: number }>) => {
            state.characterId = action.payload.characterId;
            state.characterName = action.payload.characterName
        },

        setCharacterLevel: (state, action: PayloadAction<{ characterLevel: number, characterExp: number }>) => {
            state.characterLevel = action.payload.characterLevel;
            state.characterExp = action.payload.characterExp;
        },

        setCharacterSheetValue: (state, action: PayloadAction<SetCharacterSheetValueInput>) => {
            state.CharacterSheet.initiative = action.payload.initiative;
            state.CharacterSheet.armorClass = action.payload.armorClass;
            state.CharacterSheet.currentHitpoints = action.payload.currentHitpoints;
            state.CharacterSheet.temporaryHitpoints = action.payload.temporaryHitpoints;
            state.CharacterSheet.currentMana = action.payload.currentMana;
            state.CharacterSheet.lifepoints = action.payload.lifepoints;
            state.CharacterSheet.spellAttackModifier = action.payload.spellAttackModifier;
            state.CharacterSheet.spellSaveDc = action.payload.spellSaveDc;
            state.CharacterSheet.languages = action.payload.languages;
        },

        setAbilityValue: (state, action: PayloadAction<AbilityInput>) => {
            const index = state.CharacterSheet.ability_to_characterSheet.findIndex(ability => Number(ability.ability_to_characterSheetId) === Number(action.payload.ability_to_characterSheetId))
            state.CharacterSheet.ability_to_characterSheet[index].value = action.payload.value;
            state.CharacterSheet.ability_to_characterSheet[index].proficiencyAmount = action.payload.proficiencyAmount;
        },

        setSkillValue: (state, action: PayloadAction<SkillInput>) => {
            const index = state.CharacterSheet.skill_to_characterSheet.findIndex(skill => Number(skill.skill_to_characterSheetId) === Number(action.payload.skill_to_characterSheetId))
            state.CharacterSheet.skill_to_characterSheet[index].value = action.payload.value;
            state.CharacterSheet.skill_to_characterSheet[index].proficiencyAmount = action.payload.proficiencyAmount;
        },

        addNewSpell: (state, action: PayloadAction) => {
            state.CharacterSheet.Spell.push({
                spellId: (state.CharacterSheet.Spell.length) * -1,
                spellName: "",
                spellText: "",
                isConcentration: false,
                isActive: false,
                spellTarget: "",
                spellDuration: "",
                spellCost: 0,
                spellComponents: "",
                spellType: 0,
                spellListType: {
                    spellListTypeId: 0,
                    spellListTypeName: "",
                    Ability: {
                        abilityId: 0,
                        abilityName: "",
                    }
                },
                savingThrow: {
                    abilityName: "",
                    abilityId: 0
                }
            })
        },

        removeSpell: (state, action: PayloadAction<number>) => {
            const index = state.CharacterSheet.Spell.findIndex(spell => Number(spell.spellId) === Number(action.payload))
            state.CharacterSheet.Spell.splice(index, 1)
        },

        changeSpell: (state, action: PayloadAction<Spell>) => {
            const index = state.CharacterSheet.Spell.findIndex(spell => Number(spell.spellId) === Number(action.payload.spellId))
            state.CharacterSheet.Spell[index].spellName = action.payload.spellName;
            state.CharacterSheet.Spell[index].spellText = action.payload.spellText;
            state.CharacterSheet.Spell[index].isConcentration = action.payload.isConcentration;
            state.CharacterSheet.Spell[index].spellTarget = action.payload.spellTarget;
            state.CharacterSheet.Spell[index].spellDuration = action.payload.spellDuration;
            state.CharacterSheet.Spell[index].spellCost = action.payload.spellCost;
            state.CharacterSheet.Spell[index].spellComponents = action.payload.spellComponents;
            state.CharacterSheet.Spell[index].spellType = action.payload.spellType;
            state.CharacterSheet.Spell[index].savingThrow.abilityName = action.payload.savingThrow.abilityName;
            state.CharacterSheet.Spell[index].savingThrow.abilityId = action.payload.savingThrow.abilityId;
            state.CharacterSheet.Spell[index].spellListType = action.payload.spellListType;
            state.CharacterSheet.Spell[index].isActive = action.payload.isActive;
        },

        addNewItem: (state, action: PayloadAction) => {
            state.CharacterSheet.Item.push({
                itemId: (state.CharacterSheet.Item.length) * -1,
                itemDescription: "",
                itemAmount: 0
            })
        },

        removeItem: (state, action: PayloadAction<number>) => {
            const index = state.CharacterSheet.Item.findIndex(item => Number(item.itemId) === Number(action.payload))
            state.CharacterSheet.Item.splice(index, 1)
        },

        changeItem: (state, action: PayloadAction<Item>) => {
            const index = state.CharacterSheet.Item.findIndex(item => Number(item.itemId) === Number(action.payload.itemId))
            state.CharacterSheet.Item[index].itemDescription = action.payload.itemDescription;
            state.CharacterSheet.Item[index].itemAmount = action.payload.itemAmount;
        },

        addNewNote: (state, action: PayloadAction) => {
            state.CharacterSheet.Note.push({
                noteId: (state.CharacterSheet.Note.length) * -1,
                noteHeading: "",
                noteText: ""
            })
        },

        removeNote: (state, action: PayloadAction<number>) => {
            const index = state.CharacterSheet.Note.findIndex(note => Number(note.noteId) === Number(action.payload))
            state.CharacterSheet.Note.splice(index, 1)
        },

        changeNote: (state, action: PayloadAction<Note>) => {
            const index = state.CharacterSheet.Note.findIndex(note => Number(note.noteId) === Number(action.payload.noteId))
            state.CharacterSheet.Note[index].noteHeading = action.payload.noteHeading;
            state.CharacterSheet.Note[index].noteText = action.payload.noteText;
        },

        setSubclass: (state, action: PayloadAction<{ subclassId: number, subclassName: string, subclassDescription: string, subClassSpellCast: string }>) => {
            console.log("Subklasse setzen")
        },
        
        setCharacterSilver: (state, action: PayloadAction<number>) => {
            state.CharacterSheet.silver = action.payload
        },

        setCharacterGold: (state, action: PayloadAction<number>) => {
            state.CharacterSheet.gold = action.payload
        },
        
        spendMana: (state, action: PayloadAction<number>) => {
            const manaDiff = state.CharacterSheet.currentMana - action.payload;
            if (manaDiff >= 0) {
                state.CharacterSheet.currentMana = manaDiff
            }
        }
    }
});

export const {
    setCharacter,
    setCharacterValues,
    setAbilityValue,
    setSkillValue,
    setCharacterSheetValue,
    setCharacterLevel,
    addNewItem,
    addNewNote,
    addNewSpell,
    removeSpell,
    removeNote,
    removeItem,
    changeSpell,
    changeNote,
    changeItem,
    setSubclass,
    setCharacterSilver,
    setCharacterGold,
    spendMana
} = characterSlice.actions

export default characterSlice.reducer